// S5 data the wizard hooks do not already cover (Module 02 §4 S5), all through
// the generated api-client (doctrine 3 — no hand-rolled fetch, no DB).
//
// Two reads, both genuinely needed by the screen and neither owned by this
// module:
//
//  1. The COMMITTED org tree (Module 16: `/v1/org/facilities`,
//     `/v1/org/facilities/{id}/departments`, `/v1/org/work-centers`). §3.4
//     diffs the draft against it by code, so without it the screen could not
//     say what a commit will create — and, decisively, could not show WHICH
//     committed nodes a `confirm_deletions` commit would archive. A destructive
//     option that cannot name its targets is exactly the "silent deletion" the
//     spec forbids, so when this read is unavailable (a role without
//     `identity.view_org`) the screen reports that and keeps the confirm box
//     switched off.
//  2. The installed pack's work-center templates, for the §4 S5 template picker.
//     `GET /v1/verticals/{key}` returns the whole pack as an untyped record
//     (`VerticalPackDetail`), so the templates are read defensively — a pack
//     without them yields an empty list, never a crash.

import { useQueries, useQuery } from "@tanstack/react-query";
import type { components } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { RequestError, unwrap } from "../../../../lib/request";
import { useCurrentInstall, useVertical } from "../../hooks/onboarding-queries";
import type { ExistingTree } from "./plant-draft";

type S = components["schemas"];

const ORG_KEY = ["onboarding", "plant", "org"] as const;

/** Result of the committed-tree read, with "not permitted" as a real state. */
export interface ExistingTreeResult {
  readonly tree: ExistingTree;
  readonly isPending: boolean;
  readonly isError: boolean;
  /** True when the caller may not read the org tree (403) — not an error card. */
  readonly forbidden: boolean;
  readonly error: unknown;
  readonly refetch: () => void;
}

function isForbidden(error: unknown): boolean {
  return error instanceof RequestError && error.httpStatus === 403;
}

/**
 * The committed facilities → departments → work centers, EVERY status.
 *
 * These reads used to pass `status=active`, which made the screen blind to the
 * one thing §3.4's server is not: an ARCHIVED row whose code the draft reuses.
 * The server matches by code across all statuses and un-archives what it finds
 * (`plant-map.service`), so an active-only client predicted "will create 1" and
 * then announced "Committed 1 new nodes" over a 200 body that said
 * `{created:{work_centers:0}, updated:1}` — the screen telling two stories about
 * one commit (UX-1 surface review F2). Recycling a decommissioned line's code is
 * ordinary factory practice, so this is not a corner.
 *
 * Every consumer that wants only live rows filters for itself and says so:
 * `committedIndex` (tree-editor) and `diffPlan`'s matched/absent both keep their
 * `status === "active"` predicates. `diffPlan` alone reads the archived rows,
 * to classify a code collision as a RESTORE.
 *
 * Departments are per-facility endpoints (and already return every status), so
 * they fan out with `useQueries` after the facility list resolves.
 */
export function useExistingPlantTree(): ExistingTreeResult {
  const facilities = useQuery({
    queryKey: [...ORG_KEY, "facilities"],
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/org/facilities", { signal }), "org-facilities"),
  });

  const workCenters = useQuery({
    queryKey: [...ORG_KEY, "work-centers"],
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/org/work-centers", { signal }), "org-work-centers"),
  });

  // The department fan-out stays on ACTIVE facilities. The facility and
  // work-center lists are one request each, so reading every status there is
  // free; departments are one request PER FACILITY, and the design partner's
  // demo tenant already carries 23 archived facilities beside its 4 active ones
  // — descending into them would be 23 extra round trips on every load of this
  // screen, gating the §5 atomic reveal behind all of them and spending the
  // 2.0 s route-interactive budget on history.
  //
  // What that costs, stated: an archived DEPARTMENT of an ACTIVE facility is
  // still covered (this endpoint carries no status filter and returns every
  // row), which is the case a real tenant hits — a department decommissioned
  // and later re-created by code. Not covered: the departments of an ARCHIVED
  // facility. In that case the FACILITY is already classified as a restore from
  // the flat list, which is the headline the user needs; its children are
  // predicted as creations and the server's own answer corrects the record in
  // the success banner, which is the guarantee that matters (F2's fix is that
  // the banner reports the SERVER, never the prediction).
  const facilityIds = (facilities.data?.items ?? [])
    .filter((f) => f.status === "active")
    .map((f) => f.id);
  const departmentQueries = useQueries({
    queries: facilityIds.map((id) => ({
      queryKey: [...ORG_KEY, "departments", id],
      retry: false,
      queryFn: async ({ signal }) =>
        unwrap(
          await client.GET("/v1/org/facilities/{id}/departments", {
            signal,
            params: { path: { id } },
          }),
          "org-departments",
        ),
    })),
  });

  const departments: S["Department"][] = departmentQueries.flatMap((q) => q.data?.items ?? []);
  const errors = [facilities.error, workCenters.error, ...departmentQueries.map((q) => q.error)];
  const firstError = errors.find((e) => e !== null);
  const forbidden = errors.some(isForbidden);

  return {
    tree: {
      facilities: facilities.data?.items ?? [],
      departments,
      workCenters: workCenters.data?.items ?? [],
    },
    isPending:
      facilities.isPending || workCenters.isPending || departmentQueries.some((q) => q.isPending),
    isError: firstError !== undefined && !forbidden,
    forbidden,
    error: firstError,
    refetch: () => {
      void facilities.refetch();
      void workCenters.refetch();
      for (const q of departmentQueries) void q.refetch();
    },
  };
}

// ---- pack work-center templates (§4 S5 template picker) ---------------------

export interface WorkCenterTemplateOption {
  readonly ref: string;
  /** Locale-resolved label, falling back to the ref (never a blank option). */
  readonly label: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Read `work_center_templates[]` out of the untyped pack document. The pack
 * contract (`packages/verticals` WorkCenterTemplate) is `{ref, labels:{en,ru,
 * zh,fr}, …}`; anything that does not match that shape is skipped rather than
 * rendered as a broken option.
 */
export function readWorkCenterTemplates(pack: unknown, locale: string): WorkCenterTemplateOption[] {
  if (!isRecord(pack)) return [];
  const raw = pack["work_center_templates"];
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): WorkCenterTemplateOption[] => {
    if (!isRecord(entry)) return [];
    const ref = entry["ref"];
    if (typeof ref !== "string" || ref.length === 0) return [];
    const labels = entry["labels"];
    let label = ref;
    if (isRecord(labels)) {
      const localized = labels[locale];
      const english = labels["en"];
      if (typeof localized === "string" && localized.length > 0) label = localized;
      else if (typeof english === "string" && english.length > 0) label = english;
    }
    return [{ ref, label }];
  });
}

export interface WorkCenterTemplatesResult {
  readonly options: readonly WorkCenterTemplateOption[];
  readonly isPending: boolean;
  /** True when no pack is installed yet — the picker says so (§3.2 → §3.4). */
  readonly noPack: boolean;
}

/** The installed pack's work-center templates, locale-resolved. */
export function useWorkCenterTemplates(locale: string): WorkCenterTemplatesResult {
  const install = useCurrentInstall();
  const packKey = install.data?.install.pack_key ?? "";
  const pack = useVertical(packKey);
  return {
    options: readWorkCenterTemplates(pack.data, locale),
    isPending: install.isPending || (packKey.length > 0 && pack.isPending),
    noPack: install.isFetched && install.data === null,
  };
}

// ---- pack routing-step templates (§4 S5 "Bind work centers") ----------------

export interface RoutingStepTemplateOption {
  readonly ref: string;
  readonly label: string;
}

/**
 * Read `routing_steps[]` out of the untyped pack document (the pack contract's
 * `RoutingStepTemplate` is `{ref, labels:{en,ru,zh,fr}, sequence_hint, …}`).
 * Ordered by `sequence_hint` so the bind grid reads in process order, which is
 * how a factory thinks about its steps. Malformed entries are skipped rather
 * than rendered as broken rows.
 */
export function readRoutingStepTemplates(
  pack: unknown,
  locale: string,
): RoutingStepTemplateOption[] {
  if (!isRecord(pack)) return [];
  const raw = pack["routing_steps"];
  if (!Array.isArray(raw)) return [];
  const rows = raw.flatMap((entry): { option: RoutingStepTemplateOption; order: number }[] => {
    if (!isRecord(entry)) return [];
    const ref = entry["ref"];
    if (typeof ref !== "string" || ref.length === 0) return [];
    const labels = entry["labels"];
    let label = ref;
    if (isRecord(labels)) {
      const localized = labels[locale];
      const english = labels["en"];
      if (typeof localized === "string" && localized.length > 0) label = localized;
      else if (typeof english === "string" && english.length > 0) label = english;
    }
    const hint = entry["sequence_hint"];
    return [{ option: { ref, label }, order: typeof hint === "number" ? hint : 0 }];
  });
  rows.sort((a, b) => a.order - b.order);
  return rows.map((r) => r.option);
}

export interface RoutingStepTemplatesResult {
  readonly options: readonly RoutingStepTemplateOption[];
  readonly isPending: boolean;
  readonly noPack: boolean;
}

export function useRoutingStepTemplates(locale: string): RoutingStepTemplatesResult {
  const install = useCurrentInstall();
  const packKey = install.data?.install.pack_key ?? "";
  const pack = useVertical(packKey);
  return {
    options: readRoutingStepTemplates(pack.data, locale),
    isPending: install.isPending || (packKey.length > 0 && pack.isPending),
    noPack: install.isFetched && install.data === null,
  };
}
